import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { earnDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { EarnDirectDetailsSummary } from './components/direct/EarnDirectDetailsSummary'
import { EarnDetailsHeader } from './components/EarnDetailsHeader'
import { EarnEmbeddedDetailsCustomerSummary } from './components/embedded/EarnEmbeddedDetailsCustomerSummary'
import { EarnEmbeddedDetailsMerchantSummary } from './components/embedded/EarnEmbeddedDetailsMerchantSummary'
import { EarnHistory } from './components/history/EarnHistory'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnDetailsData } from './hooks/useEarnDetailsData'
import { useEarnTracking } from './hooks/useEarnTracking'
import { EARN_MODEL, EARN_SHEET_ACTION_TYPE, EarnDetailsAsset } from './types'
import { EARN_DETAILS_STATE } from './types/detailsState'

export const EarnDetails = () => (
  <EarnSheetProvider>
    <EarnDetailsContent />
  </EarnSheetProvider>
)

const EarnDetailsContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setEarnDetailsBreadcrumb = useSetAtom(earnDetailsBreadcrumbAtom)
  const { handleSheetOpen } = useEarnSheetContext()
  const { trackPageView } = useEarnTracking()

  const handleDeposit = (asset: EarnDetailsAsset) => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
      header: t('earn.depositSheet.header'),
      selectedCurrency: asset.asset,
      portfolioId: asset.portfolioId,
      productName: asset.productName,
    })
  }

  const handleWithdraw = (asset: EarnDetailsAsset) => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
      header: t('earn.withdrawSheet.header'),
      selectedCurrency: asset.asset,
      portfolioId: asset.portfolioId,
      productName: asset.productName,
    })
  }

  const detailsData = useEarnDetailsData({
    onDeposit: handleDeposit,
    onWithdraw: handleWithdraw,
  })
  const { isStatisticsLoading, earnModel, portfolioId, detailsState } = detailsData
  const isDirectEarning = earnModel === EARN_MODEL.DIRECT
  const asset = detailsState === EARN_DETAILS_STATE.READY ? detailsData.asset : ''

  useEffect(() => {
    setEarnDetailsBreadcrumb(asset)
    return () => setEarnDetailsBreadcrumb('')
  }, [asset, setEarnDetailsBreadcrumb])

  useEffect(() => {
    trackPageView('details', { portfolioId })
  }, [trackPageView, portfolioId])

  if (detailsState === EARN_DETAILS_STATE.LOADING) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (detailsState === EARN_DETAILS_STATE.NOT_FOUND) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  const {
    walletId,
    epcTakesEarn,
    earnRate,
    customerEarnRate,
    summary,
    totalEarnings,
    customerSummary,
    onDeposit,
    onWithdraw,
  } = detailsData

  if (isDirectEarning) {
    return (
      <>
        <EarnDetailsHeader asset={asset} onWithdrawClick={onWithdraw} onDepositClick={onDeposit} />
        <Box flexCol gap={24}>
          <EarnDirectDetailsSummary
            earnRate={earnRate}
            allocatedFunds={summary.allocatedFunds}
            myLifetimeEarnings={totalEarnings.allocationEarnings}
            unallocatedFunds={summary.unallocatedFunds}
            isStatisticsLoading={isStatisticsLoading}
          />
          <EarnHistory portfolioId={portfolioId} walletId={walletId} />
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnDetailsHeader asset={asset} onWithdrawClick={onWithdraw} onDepositClick={onDeposit} />
      <Box flexCol gap={24}>
        <Box flexCol gap={16} align="start" className=" lg:flex-row">
          <EarnEmbeddedDetailsMerchantSummary
            earnRate={earnRate}
            allocatedFunds={summary.allocatedFunds}
            allocationEarnings={totalEarnings.allocationEarnings}
            totalEarnings={totalEarnings.totalEarnings}
            customerEarnings={totalEarnings.customerEarnings}
          />

          <EarnEmbeddedDetailsCustomerSummary
            customerEarnRate={customerEarnRate}
            customerEarningBalance={customerSummary.customerEarningBalance}
            customerPaidAmount={customerSummary.customerPaidAmount}
            epcTakesEarn={epcTakesEarn}
            isStatisticsLoading={isStatisticsLoading}
          />
        </Box>

        <EarnHistory portfolioId={portfolioId} walletId={walletId} />
      </Box>
    </>
  )
}
